import { Modal } from "@/components/Modal"
import { Popover } from "@/components/Popover"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { Close, View } from "@/Images"
import { IBANApi, IBANTransaction } from "@usher/pe-client-front-end-lib"
import { clsx } from "clsx"
import { PropsWithChildren, useCallback, useState } from "react"
import { NavLink } from "react-router-dom"
import { Fragment } from "react/jsx-runtime"
import { resolveStatus } from "./resolveStatus"

interface Props {
  transaction: IBANTransaction
}

const InfoRow = ({ title, children, className }: PropsWithChildren<{ title: string; className?: string }>) => {
  return (
    <div className="text-[14px]">
      <p className="font-bold text-gray-400">{title}</p>
      <p style={{ color: "black", wordWrap: "break-word" }}>
        <span className={clsx(className)}>{children ?? ""}</span>
      </p>
    </div>
  )
}
interface ExTransaction extends IBANTransaction {
  wallet: string
  amount_sent: string
  commission_amount: string
  ex_transaction_id: string
  crypto_symbol: string
  transaction_type: string
}
const ViewTarget = ({ onViewClick }: { onViewClick: VoidFunction }) => {
  return (
    <div className="rounded-full p-2 hover:bg-gray-100 cursor-pointer max-w-max" onClick={onViewClick}>
      <View />
    </div>
  )
}
const TransactionInfoBody = ({ transaction, close, data }: Props & { close: VoidFunction; data: ExTransaction | null }) => {
  const isTopUp = transaction.status.toLowerCase().includes("top up")
  const isIncoming = transaction.transaction_type.toLowerCase().includes("incoming")
  const hasFee = transaction.total_fee && Number(transaction.total_fee) > 0
  const hasTopUpFee = data?.commission_amount && Number(data.commission_amount) > 0
  const recepientInfo = `${transaction.receiver_name ?? ""} ${transaction.receiver_iban ?? ""}`
  const senderInfo = `${transaction.sender_name ?? ""} ${transaction.sender_iban ?? ""}`
  const senderBankInfo = `${transaction.sender_bank_name ?? ""} ${transaction.sender_bank_address ?? ""}`
  const recepientBankInfo = `${transaction.receiver_bank_name ?? ""} ${transaction.receiver_bank_address ?? ""}`

  return (
    <div className="">
      <div className="flex items-center w-full justify-between border-b border-[#f0f5f9]">
        <div className="view-hover_tilte">{isTopUp ? "Top Up" : transaction.transaction_type}</div>
        <div onClick={close} className="mr-3 cursor-pointer p-2">
          <Close />
        </div>
      </div>
      <div className="p-[20px] flex flex-col gap-[10px]">
        <InfoRow title="Transaction ID:"># {transaction.id}</InfoRow>
        {isTopUp && data?.ex_transaction_id != null && (
          <InfoRow title="Exchange Transaction ID:" className="text-blue-500">
            <NavLink to="/transactions/exchange">
              <span style={{ textDecoration: "underline" }}># {data?.ex_transaction_id}</span>
            </NavLink>
          </InfoRow>
        )}
        <InfoRow title="Created At:">{transaction.date_created}</InfoRow>
        <InfoRow title={isTopUp ? "Top Up Amount:" : "Amount"} className={isIncoming ? "text-green-600" : "text-red-500"}>
          {Number(isTopUp ? data?.amount : (transaction.amount ?? 0)).toFixed(2)} {transaction.currency}
        </InfoRow>

        {!isTopUp && (
          <InfoRow title="Fee:" className={hasFee ? "text-red-500" : "text-gray-400"}>
            {Number(transaction?.total_fee ?? 0).toFixed(2)} {transaction?.currency}
          </InfoRow>
        )}
        {isTopUp && (
          <InfoRow title="Amount Sent:">
            {Number(data?.amount_sent ?? 0).toFixed(5)} {data?.crypto_symbol}
          </InfoRow>
        )}
        {isTopUp && (
          <InfoRow title="Fee:" className={hasTopUpFee ? "text-red-500" : "text-gray-400"}>
            {Number(data?.commission_amount ?? 0).toFixed(5)} {data?.crypto_symbol}
          </InfoRow>
        )}
        {isTopUp && <InfoRow title="Wallet:">{data?.wallet} </InfoRow>}
        {!isTopUp && <InfoRow title={`${isIncoming ? "Sender" : "Recepient"} Info:`}>{isIncoming ? senderInfo : recepientInfo}</InfoRow>}
        {!isTopUp && <InfoRow title={`${isIncoming ? "Sender" : "Recepient"} Bank Info:`}>{isIncoming ? senderBankInfo : recepientBankInfo}</InfoRow>}
        {/* @ts-ignore */}
        {!isTopUp && (
          <InfoRow title="Description:">
            {/* @ts-ignore */}
            {transaction.description ?? ""}
          </InfoRow>
        )}
        <InfoRow title="Status:" className={clsx(resolveStatus(transaction.status))}>
          {transaction.status}
        </InfoRow>
      </div>
    </div>
  )
}

export const TransactionInfo = ({ transaction }: Props) => {
  const [showModal, setShowModal] = useState(false)
  const isMobile = useMediaQuery("(max-width: 768px)")
  const [data, setData] = useState<ExTransaction | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false)
  const isTopUp = transaction.status.toLowerCase().includes("top up")
  const fetchDetails = useCallback(async () => {
    if (isMobile) setShowModal(true)
    if (isTopUp) {
      setLoading(true)
      const result = await IBANApi.fetchIbanTransactionDetails(transaction.id)
      setData(result as unknown as ExTransaction)
      setLoading(false)
    }
  }, [transaction, isMobile, isTopUp])
  if (isMobile)
    return (
      <Fragment>
        <ViewTarget onViewClick={fetchDetails} />
        <Modal show={showModal} onClose={() => setShowModal(true)} className="w-full sm:w-auto sm:min-w-[450px] p-1">
          <TransactionInfoBody transaction={transaction} close={() => setShowModal(false)} data={data} />
        </Modal>
      </Fragment>
    )
  return (
    <Fragment>
      <Popover className="mt-0 flex flex-col overflow-auto" target={<ViewTarget onViewClick={fetchDetails} />}>
        {(close) => <TransactionInfoBody transaction={transaction} close={close} data={data} />}
      </Popover>
    </Fragment>
  )
}
